import {useEffect, useState} from "react";
import * as React from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Badge, Button, FormHelperText, Grid, IconButton, Stack} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import PreviousNextButton from "../../../../../components/PreviousNextButton";
import {RequestVisitorDto} from "../../../../../modules/vms/dto/RequestVisitorDto";
import {ValidateUtils} from "../../../../../utils/ValidateUtils";
import FormFieldSelect from "../../../../../components/FormFieldSelect";
import {VisitorDriveType} from "../../../../../modules/vms/enums/VisitorDriveType";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import PlateNumberDialog from "../../../../../components/PlateNumberDialog";
import FormFieldRadio from "../../../../../components/FormFieldRadio";
import {IdTypes} from "../../../../../modules/vms/enums/IdTypes";
import StringUtils from "../../../../../utils/StringUtils";

export type RegisterVisitorFormProps = {
    requestVisitor: RequestVisitorDto;
    onPrevious: () => void;
    onNext: (requestVisitor: RequestVisitorDto) => void;
    fromEmployeeRegisterFlg?:boolean;
};

const RegisterVisitorForm: React.FC<RegisterVisitorFormProps> = (props) => {
    const intl = useIntl();
    const [messageMap, setMessageMap] = useState(new Map<string, string>());
    const [innerRequestVisitor, setInnerRequestVisitor] = useState<RequestVisitorDto>(props.requestVisitor);
    const [openPlateNumberDialog, setOpenPlateNumberDialog] = useState(false);

    useEffect(() => {
        setInnerRequestVisitor({...props.requestVisitor});
    }, [props.requestVisitor]);

    const _handleChange = (name: string, value: any) => {
        setInnerRequestVisitor({...innerRequestVisitor, [name]: value});
    };

    const _handlePrevious = () => {
        props.onPrevious();
    }

    const _handleNext = () => {
        if (!validate()) {
            return;
        }
        props.onNext(innerRequestVisitor);
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if(props.fromEmployeeRegisterFlg){
            if (innerRequestVisitor.remarks === '') {
                newMessageMap.set('remarks', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_EMPLOYEE_NO_REQUIRED'}));
            }
        }
        if (innerRequestVisitor.idType === '') {
            newMessageMap.set('idType', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ID_TYPE_REQUIRED'}));
        }
        if (innerRequestVisitor.idNumber === '') {
            newMessageMap.set('idNumber', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ID_NUMBER_REQUIRED'}));
        }else{
            if(innerRequestVisitor.idType ===IdTypes.Enum.idCard){
                if(!ValidateUtils.isIdNumber(innerRequestVisitor.idNumber)){
                    newMessageMap.set('idNumber', intl.formatMessage({id: 'VMS_VISITOR_MSG_ID_NUMBER_FORMAT_ERROR'}));
                }
            }
        }
        if (innerRequestVisitor.lastName === '') {
            newMessageMap.set('lastName', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_LAST_NAME_REQUIRED'}));
        }
        if (innerRequestVisitor.firstName === '') {
            newMessageMap.set('firstName', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_FIRST_NAME_REQUIRED'}));
        }
        if (innerRequestVisitor.mobile === '') {
            newMessageMap.set('mobile', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MOBILE_REQUIRED'}));
        } else if (!ValidateUtils.isMobileNumber(innerRequestVisitor.mobile)) {
            newMessageMap.set('mobile', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MOBILE_FORMAT_ERROR'}));
        }
        if (innerRequestVisitor.company === '') {
            newMessageMap.set('company', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_COMPANY_REQUIRED'}));
        }
        if (innerRequestVisitor.email !== '' && !ValidateUtils.isEmail(innerRequestVisitor.email)) {
            newMessageMap.set('email', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_EMAIL_FORMAT_ERROR'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <div>
            <PlateNumberDialog open={openPlateNumberDialog} onOk={(value) => _handleChange("plateNumber", value)}
                               onClose={() => setOpenPlateNumberDialog(false)}
                               value={innerRequestVisitor.plateNumber}/>
            <div>
                {props.fromEmployeeRegisterFlg?
                    <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMPLOYEE_NO"})}
                                   placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMPLOYEE_NO_REQUIRED"})}
                                   required={true} errorText={messageMap.get("remarks")}
                                   value={innerRequestVisitor.remarks}
                                   onChange={(value) => {
                                       _handleChange("remarks", value)
                                   }}/>:null
                }



                <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_ID_TYPE"})}
                                 required={true}
                                 value={innerRequestVisitor.idType}
                                 onChange={(value) => {
                                     _handleChange("idType", value)
                                 }}
                                 items={IdTypes.List.map((item) => {
                                     return {label: intl.formatMessage({id: item.label}), value: item.value}
                                 })}/>

                <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_ID_NUMBER"})}
                               placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_ID_NUMBER_REQUIRED"})}
                               required={true} errorText={messageMap.get("idNumber")}
                               value={innerRequestVisitor.idNumber}
                               onChange={(value) => {
                                   _handleChange("idNumber", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_MOBILE"})}
                               placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_MOBILE_REQUIRED"})}
                               required={true} errorText={messageMap.get("mobile")}
                               type={"number"}
                               value={innerRequestVisitor.mobile}
                               onChange={(value) => {
                                   _handleChange("mobile", value)
                               }}/>
                <Stack direction={'row'}>
                    <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_LAST_NAME"})}
                                   placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_LAST_NAME_REQUIRED"})}
                                   required={true} errorText={messageMap.get("lastName")}
                                   value={innerRequestVisitor.lastName}
                                   onChange={(value) => {
                                       _handleChange("lastName", value)
                                   }}/>
                    <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_FIRST_NAME"})}
                                   placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_FIRST_NAME_REQUIRED"})}
                                   required={true} errorText={messageMap.get("firstName")}
                                   value={innerRequestVisitor.firstName}
                                   onChange={(value) => {
                                       _handleChange("firstName", value)
                                   }}/>
                </Stack>
                <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_COMPANY"})}
                               placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_COMPANY_REQUIRED"})}
                               required={true} errorText={messageMap.get("company")}
                               value={innerRequestVisitor.company}
                               onChange={(value) => {
                                   _handleChange("company", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMAIL"})}
                               placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMAIL_TIPS"})}
                               value={innerRequestVisitor.email} errorText={messageMap.get("email")}
                               onChange={(value) => {
                                   _handleChange("email", value)
                               }}/>
                <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_DRIVE_TYPE"})}
                                 value={innerRequestVisitor.driveType}
                                 onChange={(value) => {
                                     _handleChange("driveType", value)
                                 }}
                                 items={VisitorDriveType.List.map((item) => {
                                     return {label: intl.formatMessage({id: item.label}), value: item.value}
                                 })}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_PLATE_NUMBER"})}
                               placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_PLATE_NUMBER_REQUIRED"})}
                               value={innerRequestVisitor.plateNumber}
                               endAdornment={
                                   <IconButton color="primary" onClick={() => setOpenPlateNumberDialog(true)}>
                                       <KeyboardIcon/>
                                   </IconButton>}/>
                <FormHelperText error={true}>
                    重要!!! 如您驾车来访，根据化工园区车辆管控要求请您提前10分钟于下网址登记车辆信息。超过或小于10分钟需要重新预约！
                </FormHelperText>
                <a href={'http://110.188.114.6:60013/web/#/'} target={'_blank'}>http://110.188.114.6:60013/web/#/</a>
            </div>
            <br/>
            <PreviousNextButton onPrevious={_handlePrevious} onNext={_handleNext}/>
        </div>
    )
};

export default RegisterVisitorForm;